import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';

/**
 * Shared base class for Vejir (Fan) variants
 */
abstract class VejirBase implements ILayoutLabelTextGen, IItemCount {
  constructor(protected readonly rowsPerPage: number) {}

  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const topOffset = PdfUnit.FromMillimeters(15);
    const font = new PdfFont(PdfFont.FONT_NAME, 40);

    const totalCount = Math.min(opts.list.length, this.countMax());
    const rowHeight = (PdfSize.A4.height - 2 * margin) / this.rowsPerPage;
    const rowWidth = PdfSize.A4.width - 2 * margin;

    // Calculate optimal font for labels across all items
    let labelFont: PdfFont | undefined;

    for (let pIdx = 0; pIdx < Math.ceil(totalCount / this.rowsPerPage); pIdx++) {
      const page = pdf.addPage();
      const start = pIdx * this.rowsPerPage;
      const end = Math.min(start + this.rowsPerPage, totalCount);

      if (!labelFont) {
        const allLabelTexts = opts.list
          .slice(0, totalCount)
          .map((item) => [item.label.getText() ?? '']);
        // We calculate font based on a "typical" image area.
        // In Vejir, first cell width depends on word length, but we can approximate or use a safe minimum.
        const typicalCellWidth = rowWidth / 10;
        const typicalImageRect = new PdfSize(typicalCellWidth, rowHeight - typicalCellWidth);
        labelFont = page.adjustFontMulti(allLabelTexts, GUtil.baseFont, typicalImageRect);
      }

      for (let y = 0; y < this.rowsPerPage; y++) {
        const index = start + y;
        if (index >= end) break;

        const rect = new PdfRect(margin, margin + y * rowHeight, rowWidth, rowHeight);
        const label = opts.list[index].text;

        const maxCells = 10;
        let cellWidth = rect.width / maxCells;
        if (label.length + 1 >= maxCells) {
          cellWidth = rect.width / (label.length + 1);
        }

        // First square: image with cut circle
        const r0 = new PdfRect(rect.x, rect.y, cellWidth, rect.height);
        page.cutCircle(new PdfPoint(r0.centerX, r0.bottom - r0.width / 2));
        page.drawRectangle(GUtil.cutPen, null, r0);

        // Draw label (image) in upper part of first cell
        const imageRect = new PdfRect(r0.x, r0.y, r0.width, r0.height - r0.width);
        page.drawLabel(opts.list[index].label, { rect: imageRect, font: labelFont });

        // Letter squares
        for (let i = 0; i < label.length; i++) {
          const r1 = new PdfRect(r0.right + i * cellWidth, r0.y, cellWidth, rect.height);
          page.cutCircle(new PdfPoint(r1.centerX, r1.bottom - r1.width / 2));
          page.drawRectangle(GUtil.cutPen, null, r1);

          const letterRect = new PdfRect(
            r1.x,
            r1.y + topOffset,
            r1.width,
            PdfUnit.FromMillimeters(20)
          );
          page.drawString(
            label[i].toUpperCase(),
            font,
            PdfBrush.Black,
            null,
            letterRect,
            ETextAlign.Center
          );
        }

        page.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}

/**
 * Vejir__2 (Fan) - 2 rows per page
 */
export class Vejir__2 extends VejirBase {
  constructor() {
    super(2);
  }
}

/**
 * Vejir__3 (Fan) - 3 rows per page
 */
export class Vejir__3 extends VejirBase {
  constructor() {
    super(3);
  }
}
